<template>
  <div
    :tabindex="searchable ? -1 : tabindex"
    :class="{
      'multiselect--active': isOpen,
      'multiselect--disabled': disabled,
      'multiselect--fixed': isPositionFixed,
      'multiselect--above': isAbove,
      'select-has-error': hasValidationError
    }"
    class="multiselect"
    @focus="activate()"
    @blur="searchable ? false : deactivate(); setErrorState()"
    @keydown.self.down.prevent="pointerForward()"
    @keydown.self.up.prevent="pointerBackward()"
    @keydown.enter.tab.stop.self="addPointerElement($event)"
    @keyup.esc="deactivate(); clearMatchesList();">
    <slot :toggle="toggle" name="caret">
      <div class="multiselect__select" @mousedown.prevent.stop="toggle()">
        <svg-icon name="down-default" width="11" height="11"></svg-icon>
      </div>
      <slot name="multiselect-icon"></slot>
    </slot>
    <slot :search="search" name="clear"></slot>
    <div ref="tags" :class="{'icon-field': iconField, 'has-error': hasValidationError}" class="multiselect__tags">
      <div v-show="visibleValue.length > 0" class="multiselect__tags-wrap">
        <template v-for="(option, index) of visibleValue" @mousedown.prevent>
          <slot :option="option" :search="search" :remove="removeElement" name="tag">
            <span :key="index"
                  :class="{'has-error': option.hasError, 'is-capitalized': isCapitalizedTags}"
                  class="multiselect__tag ">
              <span class="multiselect__tag__wrapper">
                <span v-text="getOptionLabel(option)"></span>
                <a class="delete-tag"
                   @keydown.enter.prevent="clearDisplayError(); removeElement(option)"
                   @mousedown.prevent="clearDisplayError(); removeElement(option)">
                  <svg-icon name="delete" width="11" height="11"></svg-icon>
                </a>
              </span>
            </span>
          </slot>
        </template>
      </div>
      <template v-if="internalValue && internalValue.length > limit">
        <strong class="multiselect__strong" v-text="limitText(internalValue.length - limit)"></strong>
      </template>
      <transition name="multiselect__loading">
        <slot name="loading"><div v-show="loading" class="multiselect__spinner"></div></slot>
      </transition>
      <slot v-if="$slots['selected-option-html'] && (!searchable || !isOpen)" name="selected-option-html"></slot>
      <input
        v-else-if="searchable"
        ref="search"
        :id="id"
        :name="name"
        :style="inputStyle"
        :value="isOpen ? search : currentOptionLabel"
        :placeholder="placeholderLabel"
        :tag-placeholder="tagPlaceholder"
        :disabled="disabled"
        :tabindex="tabindex"
        :size="inputSize"
        :class="{ big: bigInputFontSize }"
        class="multiselect__input"
        type="text"
        autocomplete="off"
        @input="clearDisplayError(); updateSearch($event.target.value)"
        @focus.prevent="activate()"
        @keydown.enter="clearMatchesList()"
        @blur.prevent="deactivate(); clearMatchesList(); $emit('blur')"
        @keyup.esc="deactivate()"
        @keydown.down.prevent="pointerForward()"
        @keydown.up.prevent="pointerBackward()"
        @keydown.enter.prevent.stop.self="addPointerElement($event)"
        @keydown.delete.stop="removeLastElement()"/>
      <span
        v-else
        class="multiselect__single"
        @mousedown.prevent="toggle"
        v-text="currentOptionLabel">
      </span>
    </div>
    <transition name="multiselect">
      <div
        v-show="isOpen && showNoResults && !loading && (filteredOptions.length || search)"
        ref="list"
        :style="{ maxHeight: optimizedHeight + 'px' }"
        class="multiselect__content-wrapper"
        @focus="activate"
        @mousedown.prevent>
        <ul :style="contentStyle" class="multiselect__content">
          <slot name="beforeList"></slot>
          <li v-if="multiple && max === internalValue.length">
            <span class="multiselect__option">
              <slot name="maxElements">
                <span v-translate="{max: max}">
                  Maximum of %{max} options selected. First remove a selected option to select another.
                </span>
              </slot>
            </span>
          </li>
          <template v-if="(!max || internalValue.length < max) && showOptionsList">
            <li v-for="(option, index) of filteredOptions"
                :key="index"
                :class="{separator: option.isSeparator}"
                class="multiselect__element"
            >
              <span
                v-if="!(option && (option.$isLabel || option.$isDisabled))"
                :class="optionHighlight(index, option)"
                :data-select="selectLabelTextCustom(option)"
                :data-selected="selectedLabelText"
                :data-deselect="deselectLabelText"
                class="multiselect__option"
                @mouseenter.self="pointerSet(index)"
                @click.stop="select(option); clearMatchesList();"
              >
                {{ option && option.isTag ? tagPlaceholder : '' }}
                <slot :option="option" :search="search" name="option">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
              <span v-if="option && (option.$isLabel || option.$isDisabled)"
                    :class="optionHighlight(index, option)"
                    class="multiselect__option multiselect__option--disabled">
                <slot :option="option" :search="search" name="option">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
            </li>
          </template>
          <li v-show="showNoResults && (filteredOptions.length === 0 && search && !loading || !showOptionsList)">
            <span class="multiselect__option">
              <slot :search="search" name="noResult">
                <span v-if="taggable" class="not-found-results">{{ tagPlaceholder }}</span>
                <span v-translate v-else class="not-found-results">No elements found. Consider changing the search query.</span>
              </slot>
            </span>
          </li>
          <slot name="afterList"></slot>
        </ul>
      </div>
    </transition>
  </div>
</template>
<script>

import '../distribution/icons/down-default';
import '../distribution/icons/delete';

import {Multiselect} from 'vue-multiselect/src';

export default {
  extends: Multiselect,
  props: {
    iconField: Boolean,
    showOptionsList: {type: Boolean, default: true},
    setErrorState: {type: Function, default: () => {}},
    clearDisplayError: {type: Function, default: () => {}},
    clearMatchesList: {type: Function, default: () => {}},
    isCapitalizedTags: {type: Boolean, default: true},
    isPositionFixed: {type: Boolean, default: false},
    bigInputFontSize: {type: Boolean, default: false},
    hasValidationError: {type: Boolean, default: false}
  },
  mounted() {
    this.$on('close', () => {
      if (this.$refs.list) {
        this.$refs.list.scrollTop = 0;
      }
    });
  },
  computed: {
    placeholderLabel() {
      return this.search || this.value.length ? '' : this.placeholder;
    },
    inputStyle() {
      return this.search || this.value.length
        ? {width: this.multiple ? 'auto' : '100%', 'max-width': '100%', position: 'relative'}
        : {width: '100%'};
    },
    inputSize() {
      return this.taggable ? this.search.length + 1 : 20;
    }
  },
  methods: {
    selectLabelTextCustom(option) {
      return this.showLabels && option && !option.isTag ? this.selectLabel : '';
    }
  }
};
</script>

<style lang="scss" scoped>
.multiselect {
  min-height: 34px;
  height: auto;
  border-radius: 3px;
  /deep/ .tag-icon {
    height: 35px;
    padding: 10px;
    position: absolute;
    display: flex;
    align-items: center;
  }
  .multiselect__element {
    color: var(--primary);
    &.selected-project {
      background-color: var(--light-gray);
      color: var(--primary);
    }
    &.separator {
      display: none;
    }
    & + .separator + li {
      border-top: 1px solid var(--mid-gray);
      padding-top: 10px;
      margin-top: 10px;
    }
  }
  .multiselect__content-wrapper {
    font-size: var(--size-12);
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    .multiselect__content {
      padding-top: 5px;
      padding-bottom: 5px;
    }
    .multiselect__option {
      padding-top: 5px;
      padding-bottom: 5px;
      min-height: 33px;
      line-height: 22px;
      white-space: normal;
      .not-found-results {
        color: var(--primary);
      }
      &.multiselect__option--highlight,
      &.multiselect__option--highlight:after {
        background: var(--secondary);
      }
      &:after {
        line-height: 22px;
        padding-top: 5px;
        padding-bottom: 5px;
      }
    }
  }
  //TODO: Refactor
  .multiselect__input.big {
    font-size: 17px;
  }
  &.multiselect--fixed {
    .multiselect__content-wrapper {
      max-width: 470px;
      position: fixed;
    }
  }
  &.multiselect--above {
    .multiselect__content-wrapper {
      position: absolute;
    }
  }
}

.multiselect__single {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 34px;
  font-size: var(--size-11);
  color: var(--black);
}
.multiselect__input {
  height: 34px;
  &::-webkit-input-placeholder {
    color: var(--primary);
    font-size: var(--size-11);
  }
  &::-moz-placeholder {
    color: var(--primary);
    font-size: var(--size-11);
  }
  &:-ms-input-placeholder {
    color: var(--primary);
    font-size: var(--size-11);
  }
  &:-moz-placeholder {
    color: var(--primary);
    font-size: var(--size-11);
  }
  &::-ms-clear {
    display: none;
  }
}
.multiselect__input,
.multiselect__single {
  margin-bottom: 0;
  padding-left: 0;
}
.multiselect--disabled {
  .multiselect__input,
  .multiselect__single,
  .multiselect__tags {
    background: var(--light-gray);
  }
  .multiselect__input::-webkit-input-placeholder {
    color: var(--mid-gray);
  }
  .multiselect__input::-moz-placeholder {
    color: var(--mid-gray);
  }
  .multiselect__input:-ms-input-placeholder {
    color: var(--mid-gray);
  }
  .multiselect__input:-moz-placeholder {
    color: var(--mid-gray);
  }
  .multiselect__select {
    background: none;
  }
}
.multiselect-icons {
  display: inline-block;
}
.multiselect__tags {
  border-radius: 3px;
  min-height: 34px;
  height: auto;
  padding: 0 40px 0 10px;
  border: 1px solid var(--mid-gray);
  &.has-error {
    border-color: var(--error);
  }
  &.icon-field {
    padding-left: 32px;
  }
  .multiselect__tag.has-error {
    background-color: var(--error);
  }
  .multiselect__tag {
    vertical-align: middle;
    margin-bottom: 5px;
    margin-top: 4px;
    margin-right: 8px;
    font-size: var(--size-12);
    border-radius: 20px;
    padding: 5px 10px 5px 10px;
    background-color: var(--secondary);
    box-shadow: 0 0 10px 0 var(--accent-button-shadow);

    @media all and (-ms-high-contrast: none) {
      *::-ms-backdrop {
        display: flex;
        align-items: center;
      }

      .multiselect__tag__wrapper {
        display: inline;
      }

      *::-ms-backdrop,
      .multiselect__tag__wrapper {
        margin-right: 0;
      } /* IE11 */
    }
    .multiselect__tag__wrapper span {
      margin-right: 20px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      -ms-text-overflow: ellipsis;
    }
    .delete-tag {
      right: 7px;
      position: absolute;
      width: unset;
      line-height: 12px;
      /deep/ .svg-icon {
        vertical-align: middle;
      }
      /deep/ .stroke {
        stroke: var(--white);
      }
    }
    @media all and (-ms-high-contrast: none) {
      *::-ms-backdrop,
      .delete-tag {
        margin-right: 0;
        right: 0;
        position: relative;
      }
    }
  }
}
.multiselect__select {
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: none;
  transition: none;
  height: 35px;
  padding-right: 0;
  right: -2px;
  /deep/ .svg-fill {
    stroke: var(--gray);
  }
  &:hover {
    /deep/ .svg-fill {
      stroke: var(--primary);
    }
  }
}
@media all and (-ms-high-contrast: none) {
  *::-ms-backdrop,
  .multiselect__select {
    /deep/ .svg-fill {
      stroke: var(--secondary);
    }
  }
}

.multiselect__select:before {
  display: none;
}
.multiselect--active {
  .multiselect__single,
  .multiselect__input {
    height: 34px;
  }
  .multiselect__tags {
    border-radius: 3px;
  }
  .multiselect__select {
    transform: none;
    /deep/ .svg-fill {
      stroke: var(--secondary);
    }
  }
}

/deep/ .multiselect__content-wrapper {
  box-shadow: 0 5px 15px var(--gray);
}
</style>
